import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import LoadingSpinner from '../components/LoadingSpinner/LoadingSpinner'
import AuthLayout from './AuthLayout'

function useTitle(title) {
  useEffect(() => {
    const prev = document.title
    document.title = title
    return () => { document.title = prev }
  }, [title])
}

const s = {
  heading: {
    fontSize: '1.6rem',
    fontWeight: 500,
    color: '#0f172a',
    marginBottom: 6,
    letterSpacing: '-0.02em',
  },
  sub: {
    fontSize: '0.9rem',
    color: '#64748b',
    marginBottom: 32,
    lineHeight: 1.6,
  },
  label: {
    display: 'block',
    fontSize: '0.8rem',
    fontWeight: 500,
    color: '#475569',
    marginBottom: 6,
    letterSpacing: '0.02em',
  },
  input: {
    width: '100%',
    padding: '10px 14px',
    border: '1px solid #d1d9e0',
    borderRadius: 8,
    fontSize: '0.95rem',
    color: '#0f172a',
    outline: 'none',
    background: '#f8fafc',
    transition: 'border-color 0.15s',
    boxSizing: 'border-box',
  },
  btn: {
    width: '100%',
    padding: '11px 0',
    background: '#84cc16',
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    fontSize: '0.95rem',
    fontWeight: 600,
    cursor: 'pointer',
    marginTop: 8,
    transition: 'background 0.2s',
  },
  error: {
    background: '#fef2f2',
    border: '1px solid #fecaca',
    borderRadius: 8,
    color: '#dc2626',
    fontSize: '0.85rem',
    padding: '10px 14px',
    marginBottom: 20,
  },
  success: {
    background: '#f0fdf4',
    border: '1px solid #bbf7d0',
    borderRadius: 8,
    color: '#16a34a',
    fontSize: '0.85rem',
    padding: '10px 14px',
    marginBottom: 20,
  },
  divider: {
    borderTop: '1px solid #f1f5f9',
    margin: '24px 0',
  },
  link: {
    color: '#475569',
    fontSize: '0.85rem',
    textDecoration: 'underline',
    cursor: 'pointer',
    background: 'none',
    border: 'none',
    padding: 0,
  },
}

function LostPassword() {
  const [email, setEmail] = useState('')
  const [processing, setProcessing] = useState(false)
  const [showError, setShowError] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [successMessage, setSuccessMessage] = useState('')
  const navigate = useNavigate()

  useTitle('Reset password — SchedPilot')

  const handleSubmit = async e => {
    e.preventDefault()
    setProcessing(true)
    setShowError(false)
    setSuccessMessage('')

    try {
      const res = await fetch('https://api.schedpilot.com/wp-json/api/v1/executeForgotPassword', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      })
      const result = await res.json()
      setProcessing(false)

      if (result.response === 200) {
        setSuccessMessage('If that email is registered, a reset link is on its way.')
        setEmail('')
      } else {
        setErrorMessage(result.reason || 'Something went wrong.')
        setShowError(true)
      }
    } catch {
      setProcessing(false)
      setErrorMessage('Server error. Please try again.')
      setShowError(true)
    }
  }

  return (
    <AuthLayout
      quote="Locked out? No problem. We'll get you back in seconds."
    >
      <p style={s.heading}>Reset your password</p>
      <p style={s.sub}>
        Enter your email address and we'll send you a link to reset your password.
      </p>

      {showError && <div style={s.error}>{errorMessage}</div>}
      {successMessage && <div style={s.success}>{successMessage}</div>}
      {processing && <LoadingSpinner />}

      <form onSubmit={handleSubmit}>
        <div style={{ marginBottom: 24 }}>
          <label style={s.label}>Email address</label>
          <input
            style={s.input}
            type="email"
            value={email}
            placeholder="you@example.com"
            onChange={e => setEmail(e.target.value)}
            onFocus={e => e.target.style.borderColor = '#84cc16'}
            onBlur={e => e.target.style.borderColor = '#d1d9e0'}
            required
          />
        </div>

        <button
          type="submit"
          style={s.btn}
          onMouseOver={e => e.target.style.background = '#65a30d'}
          onMouseOut={e => e.target.style.background = '#84cc16'}
        >
          Send reset link
        </button>
      </form>

      <div style={s.divider} />

      <p style={{ fontSize: '0.85rem', color: '#7d8fa3', textAlign: 'center', margin: 0 }}>
        <button style={s.link} onClick={() => navigate('/login')}>
          Back to sign in
        </button>
      </p>
    </AuthLayout>
  )
}

export default LostPassword
